import Anthropic from '@anthropic-ai/sdk';

// Note: In production, API key should be handled securely via environment variables
// For now, users will need to set this manually or via environment
const anthropic = new Anthropic({
  apiKey: process.env.REACT_APP_ANTHROPIC_API_KEY || '',
  dangerouslyAllowBrowser: true // Required for client-side usage
});

export interface StreamingResponse {
  onToken: (token: string) => void;
  onComplete: () => void;
  onError: (error: Error) => void;
}

export interface ApiMessage {
  role: 'user' | 'assistant';
  content: string;
}

export async function sendMessage(
  messages: ApiMessage[],
  callbacks: StreamingResponse
): Promise<void> {
  try {
    const stream = anthropic.messages.stream({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 1024,
      messages,
    });

    stream.on('text', (text: string) => {
      callbacks.onToken(text);
    });

    stream.on('end', () => {
      callbacks.onComplete();
    });

    stream.on('error', (error: Error) => {
      callbacks.onError(error);
    });

  } catch (error) {
    callbacks.onError(error as Error);
  }
}

export async function generateSuggestedTopics(
  messages: ApiMessage[],
  callbacks: StreamingResponse
): Promise<void> {
  try {

    const stream = anthropic.messages.stream({
      model: 'claude-3-5-haiku-20241022',
      max_tokens: 8192,
      messages: [
        {
          role: 'user',
          content: `
You are an AI assistant tasked with generating a list of exploratory topics to help a learner expand their understanding of a subject. Your goal is to create a diverse and engaging list of topics that will spark curiosity and provide multiple perspectives on the subject matter.

Here is the initial query provided by the learner:

<initial_query>
${messages.map(msg => msg.content).join('\n')}
</initial_query>

Your task is to generate a list of exploratory topics based on this query. Structure your final output as a JSON array of topic objects.

Instructions:

1. Generate topics that provide multidimensional perspectives, including:
   - Actionable: How to do something practical related to the subject
   - Analytical: Exploring the why and how of concepts or processes
   - Contrarian: Potential benefits of going against common advice in the field
   - Future-oriented: Examining possibilities and comparing them to the status quo
   - Comparative: Contrasting similar ideas, styles, frameworks, or approaches

2. Ensure topics cover a range of difficulty levels, from beginner to advanced.

3. Include topics highlighting real-world applications or case studies.

4. Consider interdisciplinary connections to broaden the learner's perspective.

5. Address common misconceptions or frequently asked questions in the field.

6. Propose topics that encourage critical thinking and problem-solving skills.

7. Phrase each topic as a brief, intriguing question.

8. Categorize topics into "Focused Learning" (directly relevant to the initial query) and "Curious Wandering" (seemingly irrelevant but ultimately helpful).

9. For each topic, provide a brief explanation of its relevance and how it relates to the initial query.

Structure your final output as a JSON array of topic objects, where each object contains the following fields:

- followUpQuestion: The topic phrased as a question
- relevanceDescription: Brief explanation of the topic's relevance
- curiosityMode: Either "focused" or "wandering" to allow the user to signal how far from the topic they'd like to stray.
- category: One of "actionable", "analytical", "contrarian", "future-oriented", or "comparative"

Example output structure (do not use this content in your actual response):
[
  {
    "followUpQuestion": "How does X impact Y?",
    "relevanceDescription": "Understanding the relationship between X and Y is crucial because...",
    "curiosityMode": "focused",
    "category": "analytical"
  },
  {
    "followUpQuestion": "What if we approached Z from the opposite perspective?",
    "relevanceDescription": "Exploring alternative viewpoints on Z can lead to innovative solutions...",
    "curiosityMode": "wandering",
    "category": "contrarian"
  }
]
`},
        // Pre-fills the starting character to make json outputs more consistent.
        {
          role: 'assistant',
          content: [
            {
              "type": "text",
              "text": "[ {"
            }
          ]
        }]
    });

    stream.on('text', (text: string) => {
      callbacks.onToken(text);
    });

    stream.on('end', () => {
      callbacks.onComplete();
    });

    stream.on('error', (error: Error) => {
      callbacks.onError(error);
    });

  } catch (error) {
    callbacks.onError(error as Error);
  }
}